import { isRef, toRaw, unref } from 'vue';
import { draftKeyCovers, type DraftKey } from '@/stores/draftKey';

/**
 * An Entwurf's buffer kept in the tab's `sessionStorage`, so a reload does not throw
 * the work away (TM-9664). Opt-in per panel (`useDraftForm`'s `survivesReload`):
 * ADR-0029 keeps drafts in memory, and only the Verordnung — long to type, copied off
 * paper — has asked for more.
 *
 * `sessionStorage`, not `localStorage`: the buffer holds patient and health data, so it
 * lives as long as the tab and no longer, and the logout clears it (`clearAll`).
 */

const PREFIX = 'therame:draft:';

const storageKey = (bufferKey: DraftKey): string => `${PREFIX}${bufferKey}`;

const storage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
};

/** Every buffer key that has a copy in the tab. */
const storedBufferKeys = (): DraftKey[] => {
  const store = storage();
  if (!store) return [];
  const keys: DraftKey[] = [];
  for (let index = 0; index < store.length; index++) {
    const key = store.key(index);
    if (key?.startsWith(PREFIX)) keys.push(key.slice(PREFIX.length) as DraftKey);
  }
  return keys;
};

/** The buffer's values, refs unwrapped — what JSON can carry. */
const snapshotOf = (state: object): Record<string, unknown> =>
  Object.fromEntries(Object.entries(state).map(([field, value]) => [field, toRaw(unref(value))]));

export function saveDraftBuffer(bufferKey: DraftKey, state: object): void {
  try {
    storage()?.setItem(storageKey(bufferKey), JSON.stringify(snapshotOf(state)));
  } catch {
    // Full or blocked storage loses the reload copy, never the Entwurf itself.
  }
}

/**
 * Writes a stored copy back into a freshly built buffer, field by field, so the
 * buffer keeps its own refs and reactive objects. `false` when there was nothing to
 * restore.
 */
export function restoreDraftBuffer(bufferKey: DraftKey, state: object): boolean {
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(storageKey(bufferKey)) ?? null;
  } catch {
    return false;
  }
  if (!raw) return false;
  let snapshot: Record<string, unknown>;
  try {
    snapshot = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return false;
  }
  const target = state as Record<string, unknown>;
  for (const [field, value] of Object.entries(snapshot)) {
    if (!(field in target)) continue;
    const slot = target[field];
    if (isRef(slot)) slot.value = value;
    else if (slot && typeof slot === 'object' && value && typeof value === 'object') {
      Object.assign(slot, value);
    } else target[field] = value;
  }
  return true;
}

/** Drops the copies of `key` and all its variants — the Entwurf was saved or thrown away. */
export function removeDraftBuffers(key: DraftKey): void {
  const store = storage();
  if (!store) return;
  for (const bufferKey of storedBufferKeys()) {
    if (draftKeyCovers(key, bufferKey)) store.removeItem(storageKey(bufferKey));
  }
}

/** Follows a buffer that moved — the active column graduating into the draft zone. */
export function renameDraftBuffer(from: DraftKey, to: DraftKey): void {
  const store = storage();
  if (!store) return;
  try {
    const raw = store.getItem(storageKey(from));
    if (raw === null) return;
    store.setItem(storageKey(to), raw);
    store.removeItem(storageKey(from));
  } catch {
    // As in `saveDraftBuffer`: the reload copy is a convenience, not the Entwurf.
  }
}

/** Everything, at logout: no patient's data stays in the tab for the next user. */
export function clearDraftBuffers(): void {
  const store = storage();
  if (!store) return;
  for (const bufferKey of storedBufferKeys()) store.removeItem(storageKey(bufferKey));
}
